import React, { useState, useEffect, useRef } from "react";
import Header from '../../../Components/Header/header';
import Back from '../../../Components/back/back';
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Writing from '../../../Components/typewriter/typewriter';
import '../../../css/loader.css';
import config from '../../../Context/Config';
import { toJpeg } from 'html-to-image';
import logo from '../../../Components/Assets/logo.svg'
import segLogo from '../../../Components/Assets/seglogo.svg'
import Rocket from '../../../Components/rocket/rocket';
import NextBtnLoader from "../../../Components/loader/loader";
import FormInput from "../../../Components/formInput/formInput";
import BackgruseRedirectIfNotLoggedIn from '../../../Components/redirect/redirect';
import { compressImageForIOS } from "../../../Utils/compress";
import { checkTokenAndNavigate } from "../../../Utils/login";
import Logo from '../../../Components/InfoDisplay/logo';
import HeaderTitle from '../../../Components/InfoDisplay/header';
import SignaturePopup from "../../../Components/signature/signaturePopup";
import { formattingDate } from "../../../Utils/formatDate";
import { useZoom } from "../../../Context/Zoom";
import { showAlertAndNavigate } from "../../../Utils/swal";
import { autosave } from "../../../Utils/autosave";
import useBackLock from "../../../Utils/backLock"
import Agree from '../../../Components/signature/showSignature'
import FinanicalPolicy from "../../../Components/financialPolicy/financialPolicy";
import PCMConsent from "../../../Components/privacyPolicy/pcmConsent";
import RadioGroup from "../../../Components/radio/radio";
import PatientSignatureImage from "../../../Components/signature/PatientSignatureImage";
import MedicalPortalAccess from "../../../Components/selectInput/portal";

const ConsentForm = () => {
    useBackLock();
    const { zoom, setZoom } = useZoom();
    const [nextLoader, setNextLoader] = useState(false);
    const location = useLocation();
    const { openedViaLogin, show_pcm: initialShowPcm, show_finance: initialShowFinance } = location.state || {};
    const dept_name = config.practice;
    BackgruseRedirectIfNotLoggedIn(openedViaLogin, 50, "/context-not-found");
    const navigate = useNavigate();
    const { chart, apptId } = useParams();

    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [fileUpload, setFileUpload] = useState(false);
    const [showSignaturePopup, setShowSignaturePopup] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);
    const [showAllPages, setShowAllPages] = useState(false);

    const [show_pcm, setShow_pcm] = useState(initialShowPcm || false);
    const [show_finance, setShow_finance] = useState(initialShowFinance || false);

    const taskRunRef = useRef(false);
    const pages = useRef([]);
    const pcmRef = useRef(null);
    const financeRef = useRef(null);

    const currentDate = new Date();
    const formattedDate = formattingDate(currentDate);

    // Select logo based on practice
    const practiceLogoMap = {
        'rsi': logo,
        'seg': segLogo
    };
    const currentLogo = practiceLogoMap[dept_name] || logo;

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        dob: "",
        patient_signature: "",
        pcm_consent: "",
        isMinor: false
    });

    const [signatures, setSignatures] = useState({
        patient_signature: ""
    });

    // Calculate total pages based on flags
    const getTotalPages = () => {
        let total = 0;
        if (show_pcm) total++;
        if (show_finance) total++;
        return total;
    };

    const getPageMap = () => {
        const pageMap = [];
        if (show_pcm) pageMap.push({ page: 1, type: 'pcm', ref: pcmRef });
        if (show_finance) pageMap.push({ page: show_pcm ? 2 : 1, type: 'finance', ref: financeRef });
        return pageMap;
    };

    // Validation functions
    const validatePCMPage = () => {
        if (!formData.pcm_consent) {
            Swal.fire({
                icon: "warning",
                title: "Required",
                text: "Please select Yes or No for PCM Consent",
            });
            return false;
        }

        if (formData.pcm_consent && !signatures.patient_signature) {
            Swal.fire({
                icon: "warning",
                title: "Signature Required",
                text: "Please provide your signature to proceed",
            });
            return false;
        }

        return true;
    };

    const validateFinancePage = () => {
        if (!signatures.patient_signature) {
            Swal.fire({
                icon: "warning",
                title: "Signature Required",
                text: "Signature is required to agree to Financial Responsibility",
            });
            return false;
        }
        return true;
    };

    const validateForm = (page) => {
        switch (page) {
            case 1:
                if (show_pcm) return validatePCMPage();
                if (show_finance && !show_pcm) return validateFinancePage();
                return true;
            case 2:
                if (show_pcm && show_finance) return validateFinancePage();
                return true;
            default:
                return true;
        }
    };

    // API call to fetch initial data
    const runTask = async () => {
        setIsLoading(true);
        if (taskRunRef.current) return;
        taskRunRef.current = true;

        try {
            const response = await fetch(`${config.API_BASE_URL}/token_pat_data`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: config.accessToken,
                }
            });
            if (response.ok) {
                let data = await response.json();
                setFormData(prevState => ({
                    ...prevState,
                    firstName: data['firstname'] || "",
                    lastName: data['lastname'] || "",
                    dob: data['dob'] || prevState.dob,
                    patient_signature: data.db_data?.patient_signature ?? "",
                }));

                setShow_pcm(data?.show_pcm ?? initialShowPcm ?? false);
                setShow_finance(data?.show_finance ?? initialShowFinance ?? false);

                setSignatures((prev) => ({
                    ...prev,
                    patient_signature: data.db_data?.patient_signature ?? ""
                }));

                setCurrentPage(1);

                if (!data.db_data?.patient_signature) {
                    setShowSignaturePopup(true);
                }
            } else {
                showAlertAndNavigate(
                    "warning",
                    "Unauthorized",
                    "Your session expired. Redirecting to login...",
                    config.token
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (openedViaLogin && !taskRunRef.current) {
            checkTokenAndNavigate(runTask, navigate);
        }
    }, [openedViaLogin]);

    const shouldShowSignatureAndBack = () => getTotalPages() > 1;

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    const PAGE_WIDTH = 1200;
    const SCALE = 0.7;

    const getPageRefForCurrentPage = () => {
        if (currentPage === 1 && show_pcm) return pcmRef.current;
        if (currentPage === 1 && !show_pcm && show_finance) return financeRef.current;
        if (currentPage === 2 && show_pcm && show_finance) return financeRef.current;
        return null;
    };

    const capturePageAsImage = async () => {
        try {
            const node = getPageRefForCurrentPage();
            if (!node) throw new Error("Invalid page reference");

            const originalStyle = {
                width: node.style.width,
                height: node.style.height,
                overflow: node.style.overflow,
                transform: node.style.transform,
                transformOrigin: node.style.transformOrigin,
            };

            node.style.width = `${PAGE_WIDTH}px`;
            node.style.height = "auto";
            node.style.overflow = "visible";
            node.style.transformOrigin = "top left";
            node.style.transform = `scale(${SCALE})`;

            await new Promise((resolve) => setTimeout(resolve, 50));

            const image = await toJpeg(node, {
                quality: 0.95,
                backgroundColor: "#FFFFFF",
                width: node.scrollWidth * SCALE,
                height: node.scrollHeight * SCALE,
                pixelRatio: 2,
            });

            node.style.width = originalStyle.width;
            node.style.height = originalStyle.height;
            node.style.overflow = originalStyle.overflow;
            node.style.transform = originalStyle.transform;
            node.style.transformOrigin = originalStyle.transformOrigin;

            pages.current.push(image);
            return image;
        } catch (error) {
            console.error("Error capturing page:", error);
            throw error;
        }
    };

    const uploadImageIOS = async (base64Image, url, maxRetries = 3) => {
        const compressedImage = await compressImageForIOS(base64Image, 0.8, 1200);
        const base64Data = compressedImage.replace(/^data:image\/\w+;base64,/, "");
        const binaryData = atob(base64Data);
        const arrayBuffer = new Uint8Array(binaryData.length);

        for (let i = 0; i < binaryData.length; i++) {
            arrayBuffer[i] = binaryData.charCodeAt(i);
        }

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });

                try {
                    const uploadResponse = await fetch(url, {
                        method: "PUT",
                        body: blob,
                        headers: {
                            "Content-Type": "image/jpeg",
                        },
                        keepalive: false,
                        cache: 'no-cache',
                    });

                    if (uploadResponse.ok) {
                        return uploadResponse;
                    } else {
                        throw new Error(`Fetch failed with status: ${uploadResponse.status}`);
                    }
                } catch (fetchError) {
                    const uploadResponse = await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();

                        xhr.addEventListener('load', () => {
                            if (xhr.status >= 200 && xhr.status < 300) {
                                resolve(xhr);
                            } else {
                                reject(new Error(`XHR failed with status: ${xhr.status}`));
                            }
                        });

                        xhr.addEventListener('error', () => reject(new Error('XHR network error')));
                        xhr.addEventListener('abort', () => reject(new Error('XHR upload cancelled')));

                        xhr.open('PUT', url);
                        xhr.setRequestHeader('Content-Type', 'image/jpeg');
                        xhr.timeout = 120000;
                        xhr.send(blob);
                    });

                    return uploadResponse;
                }
            } catch (error) {
                if (attempt === maxRetries) {
                    throw error;
                }
                const delay = Math.pow(2, attempt) * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    };

    const updateDatabase = async (formType = null) => {
        const type = formType || getFormTypeForPage();
        const response = await fetch(`${config.API_BASE_URL}/update_db`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: config.accessToken,
            },
            body: JSON.stringify({
                form: formData,
                show_pcm,
                show_finance,
                form_type: type
            }),
        });

        if (response.ok) {
            const data = await response.json();
            if (data?.presigned_url?.urls) {
                return data.presigned_url;
            }
            if (data?.presigned_url?.url) {
                return {
                    urls: [data.presigned_url.url],
                    keys: data.presigned_url.key ? [data.presigned_url.key] : [],
                };
            }
            if (data?.urls) {
                return data;
            }
            return data;
        } else {
            if (response.status === 401) {
                Swal.fire({
                    icon: "warning",
                    title: "Unauthorized",
                    text: "Your session expired. Click 'Restore Session' to continue.",
                    confirmButtonText: "Restore Session",
                    allowOutsideClick: false,
                }).then(() => {
                    updateDatabase();
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "There was an issue updating consent. Please try again later.",
                    confirmButtonText: "Close",
                    allowOutsideClick: false,
                });
            }
            throw new Error(`Failed to update database: ${response.status}`);
        }
    };

    const getFormTypeForPage = () => {
        if (currentPage === 1 && show_pcm) return 'consent_pcm';
        if (currentPage === 1 && !show_pcm && show_finance) return 'consent_finance';
        if (currentPage === 2 && show_pcm && show_finance) return 'consent_finance';
        return 'consent';
    };

    const handleNext = async () => {
        try {
            if (!validateForm(currentPage)) {
                return;
            }

            setNextLoader(true);
            const formType = getFormTypeForPage();
            await autosave(formType, formData);

            await capturePageAsImage();

            const totalPages = getTotalPages();
            if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
            }
        } catch (error) {
            console.error("Error on next:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });
        } finally {
            setNextLoader(false);
        }
    };

    const handleBack = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleSignaturePopupClose = () => {
        setShowSignaturePopup(false);
    };

    const handleSignatureSaveNew = (fieldName, signatureData, fullName) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: signatureData,
        }));
        setSignatures((prev) => ({
            ...prev,
            [fieldName]: signatureData,
        }));
        setShowSignaturePopup(false);
    };

    const handleSignatureSave = (fieldName, signatureData, fullName) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: signatureData,
        }));
        setSignatures((prev) => ({
            ...prev,
            [fieldName]: signatureData,
        }));
        setShowSignaturePopup(false);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm(currentPage)) {
            return;
        }

        setFileUpload(true);

        try {
            const originalZoom = zoom;
            setZoom(1);

            const totalPages = getTotalPages();
            for (let i = currentPage; i <= totalPages; i++) {
                setCurrentPage(i);
                await new Promise(resolve => setTimeout(resolve, 100));
                await capturePageAsImage();
            }

            setZoom(originalZoom);

            // Get form types for each page being submitted
            const formTypes = [];
            if (show_pcm) formTypes.push('consent_pcm');
            if (show_finance) formTypes.push('consent_finance');

            const { urls } = await updateDatabase(formTypes[0]);
            if (!urls || urls.length === 0) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No URLs returned from the server.",
                    confirmButtonText: "Close",
                    allowOutsideClick: false,
                });
                return;
            }

            const uploadTasks = urls.map(async (url, index) => {
                try {
                    const pageMap = getPageMap();
                    const pageInfo = pageMap[index];

                    if (!pageInfo || !pages.current[index]) {
                        throw new Error(`Missing page or image for index ${index}`);
                    }

                    await uploadImageIOS(pages.current[index], url);
                    return { success: true, url, type: pageInfo.type, formType: formTypes[index] };
                } catch (error) {
                    console.error(`Upload failed for index ${index}:`, error.message);
                    return { success: false, url, error };
                }
            });

            const results = [];
            const batchSize = 2;

            for (let i = 0; i < uploadTasks.length; i += batchSize) {
                const batch = uploadTasks.slice(i, i + batchSize);
                const batchResults = await Promise.all(batch);
                results.push(...batchResults);

                if (i + batchSize < uploadTasks.length) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            const failedUploads = results.filter((result) => !result.success);

            if (failedUploads.length > 0) {
                Swal.fire({
                    icon: "error",
                    title: "Upload Failed",
                    text: "Some files failed to upload. Retrying...",
                    confirmButtonText: "Retry",
                    allowOutsideClick: false,
                }).then(() => {
                    handleFormSubmit(e);
                });
            } else {
                Swal.fire({
                    icon: "success",
                    title: "Upload Complete",
                    text: "Consent forms uploaded successfully!",
                    confirmButtonText: "OK",
                    allowOutsideClick: false,
                }).then(() => {
                    navigate(`/form-submission-success`, { state: { openedViaLogin: true } });
                });
            }
        } catch (error) {
            console.error('Form submission error:', error);
            Swal.fire({
                icon: "error",
                title: "Upload Failed",
                text: `Error: ${error.message}`,
                confirmButtonText: "Retry",
                allowOutsideClick: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    handleFormSubmit(e);
                }
            });
        } finally {
            setFileUpload(false);
        }
    };

    const totalPages = getTotalPages();
    const buttonClass = "px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all";

    return (
        <div className="min-h-screen flex justify-center force-light-mode">
            <div className="min-h-screen flex flex-col bg-white w-full">
                {isLoading && <Writing />}
                {nextLoader && <NextBtnLoader />}
                {fileUpload && <Rocket />}

                <SignaturePopup
                    isOpen={showSignaturePopup}
                    onClose={handleSignaturePopupClose}
                    onSave={handleSignatureSaveNew}
                    firstName={formData.firstName}
                    lastName={formData.lastName}
                    isMinor={formData.isMinor}
                    fieldName={"patient_signature"}
                />

                <Header />
                {shouldShowSignatureAndBack() && (
                    <div className="flex justify-between items-center px-6 py-4">
                        <Back />
                    </div>
                )}

                {/* PCM Consent Page */}
                {show_pcm && currentPage === 1 && (
                    <div ref={pcmRef} className="max-w-5xl w-full bg-white mx-auto">
                        <Logo src={currentLogo} />
                        <HeaderTitle title='Principal Care Management (PCM) Consent' />
                        <div className="px-6 mx-20 py-4 bg-white">
                            <PCMConsent />
                            <div className="my-4 grid gap-3 grid-cols-1 sm:grid-cols-2">
                                <FormInput
                                    id="firstName"
                                    label="First Name"
                                    type="text"
                                    disabled={true}
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    required={false}
                                />
                                <FormInput
                                    id="lastName"
                                    label="Last Name"
                                    type="text"
                                    disabled={true}
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    required={false}
                                />
                            </div>
                            <div className="my-6 p-4 border rounded-lg bg-gray-50">
                                <label className="block text-lg font-semibold mb-4">
                                    Do you agree to Principal Care Management?
                                </label>
                                <MedicalPortalAccess
                                    id="pcm_consent"
                                    label=""
                                    portalValue={formData.pcm_consent}
                                    onChange={handleInputChange}
                                    isDisabled={fileUpload}
                                />
                            </div>
                            {formData.pcm_consent && (
                                <div className="mt-6">
                                    <PatientSignatureImage
                                        signature={formData.patient_signature}
                                        signedDate={formattedDate}
                                        label="Patient Signature"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Financial Policy Page - when only show_finance */}
                {show_finance && !show_pcm && currentPage === 1 && (
                    <div ref={financeRef} className="max-w-5xl w-full bg-white mx-auto">
                        <Logo src={currentLogo} />
                        <HeaderTitle title='Financial Responsibility Consent' />
                        <div className="px-6 mx-20 py-4 bg-white">
                            <FinanicalPolicy />
                            <div className="my-4 grid gap-3 grid-cols-1 sm:grid-cols-2">
                                <FormInput
                                    id="firstName"
                                    label="First Name"
                                    type="text"
                                    disabled={true}
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    required={false}
                                />
                                <FormInput
                                    id="lastName"
                                    label="Last Name"
                                    type="text"
                                    disabled={true}
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    required={false}
                                />
                            </div>
                            <div className="mt-6 p-4 border border-red-300 bg-red-50 rounded-lg">
                                <p className="text-red-700 font-semibold mb-4">* Signature is required to proceed</p>
                                <PatientSignatureImage
                                    signature={formData.patient_signature}
                                    signedDate={formattedDate}
                                    label="Patient Signature"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Financial Policy Page - when both show_pcm and show_finance */}
                {show_pcm && show_finance && currentPage === 2 && (
                    <div ref={financeRef} className="max-w-5xl w-full bg-white mx-auto">
                        <Logo src={currentLogo} />
                        <HeaderTitle title='Financial Responsibility Consent' />
                        <div className="px-6 mx-20 py-4 bg-white">
                            <FinanicalPolicy />
                            <div className="my-4 grid gap-3 grid-cols-1 sm:grid-cols-2">
                                <FormInput
                                    id="firstName"
                                    label="First Name"
                                    type="text"
                                    disabled={true}
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    required={false}
                                />
                                <FormInput
                                    id="lastName"
                                    label="Last Name"
                                    type="text"
                                    disabled={true}
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    required={false}
                                />
                            </div>
                            <div className="mt-6 p-4 border border-red-300 bg-red-50 rounded-lg">
                                <p className="text-red-700 font-semibold mb-4">* Signature is required to proceed</p>
                                <Agree
                                    showAgreement={true}
                                    formData={formData}
                                    title="Patient Signature"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-end space-x-2 p-5 pt-0">
                    {currentPage > 1 && (
                        <button
                            type="button"
                            onClick={handleBack}
                            className={buttonClass}
                        >
                            <i className="fas fa-arrow-left mr-2"></i> Previous
                        </button>
                    )}

                    {currentPage < totalPages ? (
                        <button
                            type="button"
                            onClick={handleNext}
                            className={buttonClass}
                        >
                            <i className="fas fa-arrow-right mr-2"></i> Next
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleFormSubmit}
                            className={buttonClass}
                        >
                            <i className="fas fa-check mr-2"></i> Submit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConsentForm;
